import { useTranslate, useMany } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Space, Progress } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  SessionListOut,
  PublicationListOut,
  ProfileOut,
  SessionEntitlementOut,
} from '@/api/generated/schemas';
import { ProductKind, ProductVariant } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { TextFilter } from '@/components/common/TextFilter';
import { PublicationSelectFilter } from '@/components/user_catalog/publications/PublicationSelectFilter';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import {
  ProductFormatTag,
  ProductVariantTag,
} from '@/components/progress/tags';
import { orDash } from '@/components/common/show/shared';
import { formatDate, formatDateTime, formatSecondsClock } from '@/utils/format';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const SessionList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<SessionListOut>({
      pagination: { pageSize: 20 },
      resource: 'progress.sessions',
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs two requests instead of one per row.
  const publicationIds = [
    ...new Set(rows.map((r) => r.publication_id).filter(Boolean)),
  ];

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  const profileIds = [
    ...new Set(rows.map((r) => r.profile_id).filter(Boolean)),
  ];

  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });

  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('progress.sessions.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...rowNav('progress.sessions', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="profile_id"
          title={t('progress.sessions.fields.profile_id')}
          render={(value: number) =>
            orDash(
              value != null && (
                <ProfileIconLink
                  profileId={value}
                  profile={profileMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="profile_id"
              placeholder={t('progress.sessions.filter.profile_id')}
              width={160}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="publication_id"
          title={t('progress.sessions.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publication_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublicationSelectFilter
              field="publication_id"
              placeholder={t('progress.sessions.filter.publication_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_kind"
          title={t('progress.sessions.fields.product_kind')}
          render={(value: string) =>
            orDash(
              value && <ProductFormatTag value={value} labels="sessions" />
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_kind')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_kind"
              minWidth={140}
              placeholder={t('progress.sessions.filter.product_kind')}
              options={Object.values(ProductKind).map((k) => ({
                label: t(`progress.sessions.format.${k}`),
                value: k,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="product_variant"
          title={t('progress.sessions.fields.product_variant')}
          render={(value: string) =>
            orDash(
              value && <ProductVariantTag value={value} labels="sessions" />
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_variant')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="product_variant"
              minWidth={140}
              placeholder={t('progress.sessions.filter.product_variant')}
              options={Object.values(ProductVariant).map((v) => ({
                label: t(`progress.sessions.variant.${v}`),
                value: v,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="covered_seconds"
          title={t('progress.sessions.fields.covered_seconds')}
          render={(value: number, record: SessionListOut) => (
            <Space size={8}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                {formatSecondsClock(value, { alwaysHours: true })}
              </span>
              <Progress
                type="circle"
                percent={
                  record.product_duration > 0
                    ? Math.round((value / record.product_duration) * 100)
                    : 0
                }
                size={48}
              />
            </Space>
          )}
        />

        <Table.Column
          dataIndex="entitlement"
          title={t('progress.sessions.fields.entitlement')}
          render={(value: SessionEntitlementOut | null) =>
            orDash(
              value &&
                `${formatDate(value.valid_from)} → ${formatDate(value.valid_until)}`
            )
          }
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('progress.sessions.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </List>
  );
};
